import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as React from "react";

import { RobotoIconButton } from "@/shared/components/RobotoIconButton";
import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import { ChatMessageStatus, FeedbackSentiment } from "@/shared/domain/chat";
import { useChatFeedbackBySequenceNum } from "@/shared/domain/chat/hooks";

import styles from "./ChatFeedback.module.css";
import { FeedbackWizardDialog } from "./FeedbackWizardDialog";
import { PriorRatingPill } from "./PriorRatingPill";

interface ChatFeedbackProps {
  chatId: string;
  /** Org the chat belongs to — forwarded to the feedback endpoints as the
   *  ``X-Roboto-Org-Id`` header so the ``REQUIRES_CALLER_ORG`` check
   *  resolves for callers with multiple org memberships. */
  chatOrgId: string;
  messageSequenceNum: number;
  messageStatus?: ChatMessageStatus;
}

export const ChatFeedback: React.FC<ChatFeedbackProps> = ({
  chatId,
  chatOrgId,
  messageSequenceNum,
  messageStatus,
}) => {
  const theme = useTheme();
  // O(1) lookup keyed by sequence number. Each ChatFeedback used to call
  // ``existingFeedback.find(...)`` which made the rendered chat O(N²) in
  // the number of assistant messages.
  const feedbackBySequenceNum = useChatFeedbackBySequenceNum(chatId, chatOrgId);
  const priorRating = feedbackBySequenceNum.get(messageSequenceNum);

  const [wizardSentiment, setWizardSentiment] =
    React.useState<FeedbackSentiment | null>(null);

  const eventContext = { chatId, messageSequenceNum };
  // ``!== COMPLETED`` rather than ``=== GENERATING`` so this matches
  // ``ForkChatButton``'s adjacent disabled rule. Both buttons are siblings
  // gated on "is this assistant message in a final state" — using two
  // different predicates let one disable while the other was active.
  const disabled = messageStatus !== ChatMessageStatus.COMPLETED;

  const composer =
    priorRating && wizardSentiment === null ? (
      <PriorRatingPill
        priorRating={priorRating}
        onEdit={() => setWizardSentiment(priorRating.sentiment)}
        eventContext={eventContext}
      />
    ) : (
      <Box className={styles.feedbackContainer}>
        <RobotoTooltip
          title={disabled ? "Wait for the response to finish" : "Good response"}
        >
          <span>
            <RobotoIconButton
              eventName="ChatFeedbackPositiveClicked"
              eventProperties={eventContext}
              onClick={() => setWizardSentiment(FeedbackSentiment.POSITIVE)}
              className={styles.iconButton}
              disabled={disabled}
              sx={{
                "&:hover": {
                  "& .MuiSvgIcon-root": {
                    color: theme.palette.success.main,
                  },
                },
              }}
            >
              <ThumbUpAltOutlinedIcon className={styles.thumbIcon} />
            </RobotoIconButton>
          </span>
        </RobotoTooltip>

        <RobotoTooltip
          title={disabled ? "Wait for the response to finish" : "Bad response"}
        >
          <span>
            <RobotoIconButton
              eventName="ChatFeedbackNegativeClicked"
              eventProperties={eventContext}
              onClick={() => setWizardSentiment(FeedbackSentiment.NEGATIVE)}
              className={styles.iconButton}
              disabled={disabled}
              sx={{
                "&:hover": {
                  "& .MuiSvgIcon-root": {
                    color: theme.palette.error.main,
                  },
                },
              }}
            >
              <ThumbDownAltOutlinedIcon className={styles.thumbIcon} />
            </RobotoIconButton>
          </span>
        </RobotoTooltip>
      </Box>
    );

  return (
    <>
      {composer}
      <FeedbackWizardDialog
        sentiment={wizardSentiment}
        chatId={chatId}
        chatOrgId={chatOrgId}
        messageSequenceNum={messageSequenceNum}
        priorRating={priorRating}
        onClose={() => setWizardSentiment(null)}
      />
    </>
  );
};
